import { useMemo } from 'react'

import { cn } from '@/shared/ui'

import { proseClasses } from './prose'
import { sanitizeFragment, sanitizeDocument } from './sanitize'

/**
 * A fragment of HTML, such as a course description, shown in the page after it is sanitized
 * again, so nothing in it runs.
 */
export function SafeHtml(props: { readonly html: string; readonly className?: string }) {
  const safe = useMemo(() => ({ __html: sanitizeFragment(props.html) }), [props.html])
  // oxlint-disable-next-line react/no-danger -- sanitized just above
  return <div className={cn(proseClasses, props.className)} dangerouslySetInnerHTML={safe} />
}

/**
 * A whole HTML document, such as a sent email, in a sandboxed frame: nothing in it can run, reach
 * the page or submit a form, and its links open in a new tab. It keeps its own styles, on white.
 * The frame scrolls inside its fixed height.
 */
export function SafeHtmlDocument(props: {
  /** Names the frame for assistive technology, such as the email's subject; also the document's title when it has none. */
  readonly title: string
  /** The document's language, such as the email's, for one that does not say. */
  readonly lang: string
  readonly html: string
  readonly className?: string
}) {
  const { title, lang, html } = props
  const safe = useMemo(() => sanitizeDocument(html, { title, lang }), [html, title, lang])
  return (
    <iframe
      title={title}
      srcDoc={safe}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      className={cn('h-[480px] w-full rounded border border-line', props.className)}
    />
  )
}
